import React, { useState, useMemo, useEffect, useCallback } from 'react';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { observer } from 'mobx-react-lite';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { AbstractCourse } from '../../../../../api/types/settings/studies/abstract-course';
import dateUtils from '../../../../../utils/date-format';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AddNewAbstractCourseDialog } from './components/add-new-abstract-course-dialog';
import { deleteAbstractCoursesOperation } from './operations/delete-abstract-courses-operation';
import { UpdateNewAbstractCourseDialog } from './components/update-abstract-course-dialog';
import { InputText } from 'primereact/inputtext';

// Separate search component to avoid observer re-renders affecting input performance
const SearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = React.memo(({ value, onChange }) => (
  <div style={{ position: 'relative', flex: 1 }}>
    <InputText
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="חיפוש קורסים לפי שם, מגמה, שעות או אחוז נוכחות..."
      style={{
        width: '100%',
        height: '30px',
        paddingLeft: '40px',
        direction: 'rtl',
      }}
      autoComplete="off"
    />
    <i
      className="pi pi-search"
      style={{
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        pointerEvents: 'none',
      }}
    />
  </div>
));

const SettingsEducationAbstractCourses = observer(() => {
  const [displayAddDialog, setDisplayAddDialog] = useState(false);

  const [deleteAbstractId, setDeleteAbstractId] = useState<number | null>(null);
  const handleDeleteProgram = async (abstractCourseId: number) => {
    await deleteAbstractCoursesOperation(abstractCourseId, setDeleteAbstractId);
  };
  const [editAbstractCourse, setEditAbstractCourse] =
    useState<AbstractCourse | null>(null);

  const [loading, setLoading] = useState(false);
  const handleEditAssignment = (abstractCourse: AbstractCourse) => {
    setEditAbstractCourse(abstractCourse);
  };

  // Search functionality
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

  // Memoized search handler to prevent unnecessary re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // Debounce search input to avoid filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchText]);

  // Filter courses based on debounced search text
  const filteredCourses = useMemo(() => {
    if (!debouncedSearchText.trim()) {
      return globalSettingsStores.abstractCourses;
    }

    const searchLower = debouncedSearchText.toLowerCase().trim();

    return globalSettingsStores.abstractCourses.filter((course) => {
      // Search in course name
      if (course.courseName.toLowerCase().includes(searchLower)) {
        return true;
      }

      if (
        course.majors.some((major) =>
          major.majorName.toLowerCase().includes(searchLower),
        )
      ) {
        return true;
      }

      // Search in course hours (convert to string)
      if (course.courseHours.toString().includes(searchLower)) {
        return true;
      }

      // Search in attendance percentage (convert to string)
      if (course.attendancePercentage.toString().includes(searchLower)) {
        return true;
      }

      // Search in creator name
      if (course.createdByName.toLowerCase().includes(searchLower)) {
        return true;
      }

      return false;
    });
  }, [debouncedSearchText, globalSettingsStores.abstractCourses]);

  return (
    <div>
      <Dialog
        header="הוספת קורס טיפוסי"
        visible={displayAddDialog}
        onHide={() => setDisplayAddDialog(false)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <AddNewAbstractCourseDialog onHide={() => setDisplayAddDialog(false)} />
      </Dialog>

      <Dialog
        header="עריכת קורס טיפוסי"
        visible={editAbstractCourse !== null}
        onHide={() => setEditAbstractCourse(null)}
        style={{
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <UpdateNewAbstractCourseDialog
          onHide={() => setEditAbstractCourse(null)}
          abstractCourseId={editAbstractCourse?.id}
          courseHours={editAbstractCourse?.courseHours}
          name={editAbstractCourse?.courseName}
          coursePercentage={editAbstractCourse?.attendancePercentage}
        />
      </Dialog>

      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          marginTop: '16px',
        }}
      >
        <SearchInput value={searchText} onChange={handleSearchChange} />

        <Button
          onClick={() => setDisplayAddDialog(true)}
          style={{ height: '30px', flexShrink: 0 }}
          label={'הוספה'}
        />
      </div>

      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={filteredCourses}
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
          marginTop: '16px',
        }}
      >
        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="courseName"
          header="שם"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          header="מגמות"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractCourse) => (
            <div>
              {rowData.majors && rowData.majors.length > 0
                ? rowData.majors.map((major) => major.majorName).join(', ')
                : 'אין מגמות'}
            </div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="courseHours"
          header="כמות שעות"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="courseHours"
          header="אחוז נוכחות"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractCourse) => (
            <div>{rowData.attendancePercentage}%</div>
          )}
        />

        <Column
          style={{ textAlign: 'right', height: '40px' }}
          field="createdAt"
          header="תאריך יצירה"
          headerStyle={{
            height: '45px',
            maxHeight: '45px',
            padding: '0',
            paddingRight: '16.5px',
          }}
          body={(rowData: AbstractCourse) => (
            <div>{dateUtils.displayFormatDate(rowData.createdAt)}</div>
          )}
        />

        <Column
          style={{ textAlign: 'right' }}
          field="נשלח ע״י"
          header=""
          body={(data: AbstractCourse) => (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                icon="pi pi-pencil"
                text
                severity="warning"
                tooltip={'עריכת סוג מטלה'}
                onClick={() => handleEditAssignment(data)}
              />
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip={'מחיקת תוכנית'}
                loading={deleteAbstractId === data.id}
                disabled={deleteAbstractId !== null}
                onClick={() => handleDeleteProgram(data.id)}
              />
            </div>
          )}
        />
      </DataTable>
    </div>
  );
});

export default SettingsEducationAbstractCourses;
